/**
 * Live Terminal Position Pricing and Holding Helpers.
 *
 * Provides real-time LTP resolution against live quote feeds, signed unrealized P&L
 * calculations, and held symbol presence verification.
 */

export interface PositionPricingInput {
  fysym?: string;
  lp?: number | null;
  netqty?: number;
  avg?: number;
}

/**
 * Resolves the freshest price for a position: prefers the live streamed feed tick,
 * falling back to the last broker-polled price.
 */
export function resolveLiveLp(
  position: PositionPricingInput | null | undefined,
  heldLtpMap: Record<string, number | null | undefined>,
): number | null {
  if (!position) return null;
  if (position.fysym && heldLtpMap && heldLtpMap[position.fysym] != null) {
    return heldLtpMap[position.fysym]!;
  }
  return position.lp || null;
}

/**
 * Calculates client-side unrealized P&L for a position.
 * Returns 0 if netqty is 0 or price is absent.
 */
export function computeUnrealizedPnl(
  position: PositionPricingInput | null | undefined,
  liveLpVal: number | null,
): number {
  if (!position || !position.netqty || position.netqty === 0) return 0;
  if (liveLpVal == null) return 0;
  const avg = position.avg || 0;
  return (liveLpVal - avg) * position.netqty; // signed netqty carries BUY/SELL sign
}

/**
 * Checks whether any open position currently holds a given contract symbol.
 */
export function isFysymHeld(
  fysym: string,
  positions:
    | Array<PositionPricingInput | null | undefined>
    | Record<string, PositionPricingInput | null | undefined>,
): boolean {
  if (!fysym) return false;
  const list = Array.isArray(positions)
    ? positions
    : Object.values(positions || {});
  return list.some((p) => p && p.fysym === fysym && (p.netqty || 0) !== 0);
}
